import { PUBLIC_POCKETBASE_URL } from '$app/env/public';
import { stripHiddenMeta, stripMeta } from '#lib/resources-strip.ts';
import type {
	DocumentDTO,
	ResourceDTO,
	ResourcesResponse,
	RetrievalContext,
	InstanceStats
} from '#src/types/documents.ts';
import { BaseApiService } from './base-api.service';

export interface HealthResponse {
	status: 'ok' | 'degraded';
	checks: Record<string, string>;
}

export class RAGService extends BaseApiService {
	constructor(pbUrl = PUBLIC_POCKETBASE_URL, authToken?: string) {
		super(`${pbUrl}/svc/rag/`, authToken);
	}

	async getHealth(): Promise<HealthResponse> {
		return this.apiFetch<HealthResponse>('GET', 'health');
	}

	// Returns 503 when degraded — don't throw, the body still contains the per-check results.
	async getExtendedHealth(): Promise<HealthResponse> {
		return this.apiFetch<HealthResponse>('GET', 'health/extended', {
			dontThrowOnBadStatus: true
		});
	}

	async getDocument(instanceId: string, documentId: number): Promise<DocumentDTO> {
		const data = await this.apiFetch<DocumentDTO>('GET', `${instanceId}/documents/${documentId}`);
		return stripHiddenMeta(data);
	}

	async getResource(instanceId: string, resourceId: number): Promise<ResourceDTO> {
		const data = await this.apiFetch<ResourceDTO>('GET', `${instanceId}/resource/${resourceId}`);
		return stripMeta(data);
	}

	async getResources(
		instanceId: string,
		page = 1,
		perPage = 20,
		{
			search = '',
			sortBy = 'date',
			sortDir = 'desc',
			resourceType = ''
		}: { search?: string; sortBy?: string; sortDir?: string; resourceType?: string } = {}
	): Promise<ResourcesResponse> {
		const params = new URLSearchParams({ page: page.toString(), per_page: perPage.toString() });
		if (search) {
			params.set('search', search);
		}
		if (sortBy) {
			params.set('sort_by', sortBy);
		}
		if (sortDir) {
			params.set('sort_dir', sortDir);
		}
		if (resourceType) {
			params.set('resource_type', resourceType);
		}

		const data = await this.apiFetch<ResourcesResponse>('GET', `${instanceId}/resources?${params}`);
		return { ...data, resources: data.resources.map(stripMeta) };
	}

	async getStats(instanceId: string): Promise<InstanceStats> {
		return this.apiFetch<InstanceStats>('GET', `${instanceId}/stats`);
	}

	async extractPdf(file: File): Promise<{ text: string; block_count: number }> {
		const formData = new FormData();
		formData.append('file', file);
		return this.apiFetch('POST', 'extract-pdf', formData);
	}

	async getRetrievalContext(
		instanceId: string,
		docIds: number[],
		chunkIds: number[]
	): Promise<RetrievalContext> {
		return this.apiFetch('POST', `${instanceId}/retrieval-context`, {
			doc_ids: docIds,
			chunk_ids: chunkIds
		});
	}
}

export const ragService = new RAGService();
